import { toString, defaultsDeep, isEmpty, isNil } from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import { v4 as uuid } from 'uuid'

import { toast } from '@/components/toasts/toast.helpers'
import { initialPlaybookBody, accessors } from '@/views/Playbooks/playbook.helpers'
import { fetchingAPI, apiErrorMessage, apiService } from '@/api'

import { buildPrompt } from '@/views/Notetaker/helpers'
import {
  buildEdgePayload,
  haveLinksChanged,
  resolveStagedOperands,
  seedFieldLinks,
} from '@/views/Notetaker/helpers/fieldLinks.helpers'
import { withTokenLinks } from '@/views/Notetaker/helpers/fieldTokens.helpers'
import { toCallTimestamp } from '@/views/Notetaker/helpers/noteComparison.helpers'
import { setLoading as setPromptLoading } from '@/reducers/prompts/prompts.redux'
import { CHANNEL_TYPE } from '@/utils/constants'
import { withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'
import { sanitizeBody, convertDurationToSeconds } from './playbook.helpers'

import * as actions from './playbook.redux'

export const fetchPlaybookVersionHelper = async (dispatch, cid) => {
  dispatch(actions.setLoading({ versions: true }))

  try {
    const versions = await fetchingAPI(
      `${apiService.core}/api/configs/${cid}/versions`,
      'GET',
      dispatch
    )

    dispatch(actions.setPlaybookVersions(versions))
  } catch (err) {
    toast.error('Failed to fetch playbook versions', { cause: err })
  } finally {
    dispatch(actions.setLoading({ versions: false }))
  }
}

export const fetchPlaybookWarningsHelper = async (dispatch, playbook) => {
  dispatch(actions.setLoading({ warnings: true }))

  try {
    if (playbook.current) {
      const warnings = await fetchingAPI(
        `${apiService.playbookWarning}/config_warnings?language=${playbook.language || 'english'}`,
        'POST',
        dispatch,
        JSON.stringify(playbook.body)
      )

      dispatch(actions.setPlaybookWarnings(warnings))
    }
  } catch (err) {
    toast.error('Failed to fetch playbook warnings', { cause: err })
  } finally {
    dispatch(actions.setLoading({ warnings: false }))
  }
}

export const fetchPlaybookWarnings = () => {
  return async (dispatch, getState) => {
    dispatch(actions.setLoading({ warnings: true }))

    try {
      const playbook = getState().playbook?.playbook

      if (playbook.current) {
        const warnings = await fetchingAPI(
          `${apiService.playbookWarning}/config_warnings?language=${
            playbook.language || 'english'
          }`,
          'POST',
          dispatch,
          JSON.stringify(playbook.body)
        )

        dispatch(actions.setPlaybookWarnings(warnings))
      }
    } catch (err) {
      toast.error('Failed to fetch playbook warnings', { cause: err })
    } finally {
      dispatch(actions.setLoading({ warnings: false }))
    }
  }
}

export const fetchNovelChecklistItems = (cid, id) => {
  return async (dispatch) => {
    try {
      const variationContent = await fetchingAPI(
        `${apiService.sayableContent}/agent-checklist?playbook_uuid=${cid}&config_id=${id}`,
        'GET',
        dispatch
      )
      const novelContent = []
      variationContent.forEach((variations) => {
        const { items, user } = variations
        const novelItems = items.filter(
          ({ item_type, created_by }) => item_type === 'novel' && created_by === user.user_id
        )

        if (!novelItems.length) {
          return
        }

        const userName = `${user.first_name} ${user.last_name}`
        novelItems.forEach((item) => {
          const totalMs = new Date(`${item.created_at}Z`).getTime()
          const novelItem = {
            ...item,
            user: userName,
            created_at: totalMs,
            name: item.text,
          }
          novelContent.push(novelItem)
        })
      })
      novelContent.sort((a, b) => a.created_at > b.created_at)
      dispatch(actions.setNovelChecklistItems(novelContent))
    } catch (err) {
      toast.error('Failed to fetch novel checklist items', { cause: err })
    }
  }
}

export const fetchPlaybookSectionWarnings = (sectionName) => {
  return async (dispatch, getState) => {
    try {
      const playbook = getState().playbook?.playbook

      if (playbook.current && sectionName === 'deck') {
        const entries = JSON.parse(JSON.stringify(playbook.body[sectionName]?.entries))
        const convertedEntries = convertDurationToSeconds(entries)

        const sectionWarnings = await fetchingAPI(
          `${apiService.playbookWarning}/section_warnings/${sectionName}?language=${
            playbook.language || 'english'
          }`,
          'POST',
          dispatch,
          JSON.stringify({ entries: convertedEntries })
        )

        dispatch(actions.setPlaybookSectionWarnings(sectionName)(sectionWarnings))
      }
    } catch (err) {
      toast.error('Failed to fetch playbook section warnings', { cause: err })
    }
  }
}

export const fetchPlaybookEntryWarnings = (sectionName, entryValues, entryId) => {
  return async (dispatch, getState) => {
    try {
      const playbook = getState().playbook?.playbook

      if (playbook.current) {
        const cleanedEntryValues = { ...entryValues }
        if (sectionName === 'deck') {
          delete cleanedEntryValues.display_duration_unit
        }

        const entryWarnings = await fetchingAPI(
          `${apiService.playbookWarning}/entry_warnings/${sectionName}?language=${
            playbook.language || 'english'
          }`,
          'POST',
          dispatch,
          JSON.stringify({
            entry_data: cleanedEntryValues,
            entry_id: entryId,
            section_entries: playbook.body[sectionName]?.entries,
          })
        )

        const playbookWarnings = getState().playbook?.warnings

        if (!isEmpty(entryWarnings) || playbookWarnings[sectionName][entryId]) {
          const payload = { entryId, entryWarnings }

          dispatch(actions.setPlaybookEntryWarnings(sectionName)(payload))
        }
      }
    } catch (err) {
      toast.error('Failed to fetch playbook entry warnings', { cause: err })
    }
  }
}

export const fetchPlaybook = (playbookId) => {
  return async (dispatch) => {
    dispatch(actions.clearValidationError())
    dispatch(actions.setLoading({ playbook: true }))

    try {
      const { config, mostRecentId, dynamic_checklist_enabled } = await fetchingAPI(
        `${apiService.core}/api/configs/${playbookId}`,
        'GET',
        dispatch
      )
      // Ensure all the default fields are present, because the API does not return with the
      // values if nothing exists
      dispatch(
        actions.setPlaybook({
          ...config,
          mostRecentId,
          dynamicChecklistEnabled: dynamic_checklist_enabled,
          body: defaultsDeep(config.body, initialPlaybookBody),
        })
      )

      await fetchPlaybookVersionHelper(dispatch, config.cid)

      try {
        await fetchPlaybookWarningsHelper(dispatch, config)
      } catch (err) {
        toast.error('Failed to fetch playbook warnings', { cause: err })
      }
      try {
        await dispatch(fetchNovelChecklistItems(config.cid, config.id))
      } catch {
        // do nothing, but don't fail
      }
    } catch (err) {
      toast.error('Failed to fetch playbook', { cause: err })
    } finally {
      dispatch(actions.setLoading({ playbook: false }))
    }
  }
}

// Make a new playbook version from an existing playbook
export const createPlaybookVersion = (playbook, history, section, readyToRestore) => {
  return async (dispatch) => {
    dispatch(actions.setLoading({ savePlaybook: true }))

    const updatedPlaybook = {
      name: playbook.name,
      cid: playbook.cid,
      began_editing: playbook.began_editing,
      organization_id: playbook.organization_id,
    }

    if (playbook.body) {
      updatedPlaybook.body = sanitizeBody(playbook.body)
    }

    try {
      const newPlaybookVersion = await fetchingAPI(
        `${apiService.core}/api/configs`,
        'POST',
        dispatch,
        JSON.stringify(updatedPlaybook)
      )

      if (history) {
        history.push(`/playbooks/${newPlaybookVersion.id}/${section}`)
        toast.success(readyToRestore ? 'Playbook restored' : 'Playbook saved!')
      }

      return newPlaybookVersion.id
    } catch (err) {
      if (err?.status === 423) {
        return toast.error(err?.message)
      }

      const { section, entry, readableError } = err

      dispatch(
        actions.setValidationError({ accessor: section, entry, errorMessage: readableError })
      )
      toast.error(
        `Failed to ${readyToRestore ? 'restore' : 'update'} playbook. Error: ${toString(
          readableError
        )}`,
        { cause: err }
      )
      return undefined
    } finally {
      // Hack to ensure playbook is not published immediately after saving to give the database time to update
      setTimeout(() => {
        dispatch(actions.setLoading({ savePlaybook: false }))
      }, 3000)
    }
  }
}

export const getScorecardsAssociatedToPlaybook = async (playbookId) => {
  try {
    const scorecardAssociations = await fetchingAPI(
      `${apiService.scorecard}/scoring/scorecards/validate_playbook/${playbookId}`,
      'GET'
    )

    return scorecardAssociations || []
  } catch (err) {
    console.error(err)
    return []
  }
}

export const updateScorecardPlaybookVersion = (id, organizationId) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.scorecard}/scoring/scorecards/update_playbook_version/${id}?requested_organization_id=${organizationId}`,
        'GET',
        dispatch
      )
    } catch (err) {
      console.error(err)
    }
  }
}

export const publishPlaybookVersion = (playbookId, showToast = true) => {
  return async (dispatch) => {
    dispatch(actions.setLoading({ savePlaybook: true }))
    let success = false

    try {
      // STEP 1: Activate the playbook version
      await fetchingAPI(`${apiService.core}/api/configs/${playbookId}`, 'PATCH', dispatch)
      dispatch(actions.activatePlaybookVersion())

      // STEP 2: Fetch scorecards associated with the new playbook
      const scorecardAssociations = await getScorecardsAssociatedToPlaybook(playbookId)

      // STEP 3: Update each scorecard with the new playbook version
      if (Array.isArray(scorecardAssociations)) {
        await Promise.all(
          scorecardAssociations.map((scorecard) =>
            dispatch(updateScorecardPlaybookVersion(scorecard.id, scorecard.organization_id))
          )
        )
      }

      if (showToast) {
        toast.success('Playbook published!')
      }
      success = true
    } catch (err) {
      toast.error('Failed to publish playbook', { cause: err })
    } finally {
      dispatch(actions.setLoading({ savePlaybook: false }))
    }

    return success
  }
}

export const fetchChecklistVariations = (playbookId, id) => {
  return async (dispatch) => {
    try {
      const checklistVariations = await fetchingAPI(
        `${apiService.sayableContent}/agent-checklist?playbook_uuid=${playbookId}&config_id=${id}`,
        'GET',
        dispatch
      )

      dispatch(actions.setChecklistVariations(checklistVariations))
    } catch (err) {
      toast.error('Failed to fetch checklist variations', { cause: err })
    }
  }
}

export const fetchChecklistItemVariations = (entryId, playbookId) => {
  return async (dispatch) => {
    try {
      const checklistItemVariations = await fetchingAPI(
        `${apiService.sayableContent}/agent-checklist?playbook_uuid=${playbookId}&checklist_properties_uuid=${entryId}`,
        'GET',
        dispatch
      )

      dispatch(actions.setChecklistItemVariations(checklistItemVariations, entryId))
    } catch (err) {
      toast.error('Failed to fetch checklist item variations', { cause: err })
    }
  }
}

export const deleteChecklistItemVariation = (userId, entryId, playbookUuid) => {
  return async (dispatch) => {
    try {
      await fetchingAPI(
        `${apiService.sayableContent}/agent-checklist`,
        'DELETE',
        dispatch,
        JSON.stringify({
          user_id: userId,
          checklist_props_uuid: entryId,
          playbook_uuid: playbookUuid,
        })
      )

      const checklistItemVariations = await fetchingAPI(
        `${apiService.sayableContent}/agent-checklist?playbook_uuid=${playbookUuid}&checklist_properties_uuid=${entryId}`,
        'GET',
        dispatch
      )

      dispatch(actions.setChecklistItemVariations(checklistItemVariations, entryId))

      toast.success('Restored checklist item to default')
    } catch (err) {
      toast.error('Failed to restore checklist item variation', { cause: err })
    }
  }
}

export const fetchAdditionalPhrases = (phrase, index, formikProps) => async (dispatch) => {
  dispatch(actions.setLoading({ smartPhraseSuggestions: index }))

  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/slp/phrase_suggestion`,
      'POST',
      dispatch,
      JSON.stringify({ ...phrase })
    )

    formikProps.setFieldValue(
      `trigger.smart_phrases[${index}].suggestions`,
      response?.suggestions || []
    )
  } catch {
  } finally {
    dispatch(actions.setLoading({ smartPhraseSuggestions: null }))
  }
}

const loadModelOptions = async (dispatch, getState) => {
  const { organizationid } = getState().currentUser

  if (organizationid === 1) {
    dispatch(actions.setLoading({ rtnModelOptions: true }))
    try {
      const modelOptions = await fetchingAPI(
        `${apiService.summary}/models?feature=rtn`,
        'GET',
        dispatch
      )
      dispatch(actions.setRTNModelOptions(modelOptions.llm_model_options))
    } finally {
      dispatch(actions.setLoading({ rtnModelOptions: false }))
    }
  }
}

// Pull every distinct field name configured for an org. These power the "reuse
// an existing field" picker in the row editor so names stay consistent across
// playbooks instead of being retyped (and subtly misspelled) each time.
export const fetchOrgFieldNames = (organizationId) => {
  return async (dispatch, getState) => {
    const orgId = organizationId || getState().currentUser.organizationid
    if (!orgId) {
      return
    }

    dispatch(actions.setLoading({ rtnFieldNames: true }))
    try {
      const fields = await fetchingAPI(
        `${apiService.summary}/bit-flows/fields?${queryString.stringify({
          requested_organization_id: orgId,
        })}`,
        'GET',
        dispatch
      )

      // Dedupe by name (a name can recur across flows) and keep its
      // display_type so the editor can show a type pill and block reusing a
      // name under a different type. First occurrence wins — a name has one
      // type org-wide. display_type is computed by the summary service (this
      // Fields endpoint exposes no field_type, so a pick_list can't be derived
      // client-side from data_type alone).
      // Options ride along: a gate on a selection field stores its operand as
      // the option's stable category uuid, so the editor needs the uuid to save
      // and the name to display, and this is the one call that has both.
      const fieldsByName = new Map()
      if (!isEmpty(fields)) {
        fields.forEach((field) => {
          if (field.field_name && !fieldsByName.has(field.field_name)) {
            fieldsByName.set(field.field_name, {
              displayType: field.display_type,
              options: field.options ?? [],
            })
          }
        })
      }

      const fieldNames = [...fieldsByName.entries()]
        .map(([name, { displayType, options }]) => ({ name, displayType, options }))
        .sort((a, b) => a.name.localeCompare(b.name))
      dispatch(actions.setRTNFieldNames(fieldNames))
    } catch (err) {
      console.error(err)
      dispatch(actions.setRTNFieldNames([]))
    } finally {
      dispatch(actions.setLoading({ rtnFieldNames: false }))
    }
  }
}

// The model selector edits the prompt backing the flow's EXTRACTION fields.
// pick_list fields also carry a prompt_uuid/prompt_id (their own prompt), so
// they must be excluded here — otherwise we could load (and on save, version)
// a pick_list field's prompt. Extraction fields normally share one prompt; if
// they ever diverge, use the prompt linked to the most extraction fields (ties
// break toward the latest, i.e. largest, prompt_id).
const getExtractionPromptId = (fields) => {
  const countsByPromptId = {}
  fields.forEach((field) => {
    if (field.field_type === 'extraction' && field.prompt_id) {
      countsByPromptId[field.prompt_id] = (countsByPromptId[field.prompt_id] ?? 0) + 1
    }
  })

  let chosenId = null
  let chosenCount = 0
  Object.entries(countsByPromptId).forEach(([promptId, count]) => {
    const id = Number(promptId)
    if (count > chosenCount || (count === chosenCount && id > chosenId)) {
      chosenId = id
      chosenCount = count
    }
  })
  return chosenId
}

const convertBitFlowFieldsToPromptFields = (fields) => {
  const sorted = [...fields].sort(
    (fieldA, fieldB) => (fieldA.order_id ?? 0) - (fieldB.order_id ?? 0)
  )
  return sorted.map((field) => ({ key: field.field_name, value: field.description }))
}

// Build the full prompt records backing a flow's extraction fields, keyed by
// prompt_uuid: the shared prompt plus each distinct "individual" extraction
// prompt (a prompt_uuid other than the shared one). Lets the editor show each
// individual field's model and version the right prompt on save. Fetched only
// for org 1 (admins), who are the only ones who see/edit per-field models.
const loadExtractionFieldPrompts = async (dispatch, fields, sharedPrompt) => {
  const fieldPrompts = {}
  if (sharedPrompt?.prompt_uuid) {
    fieldPrompts[sharedPrompt.prompt_uuid] = sharedPrompt
  }

  // One prompt_id per distinct individual prompt_uuid, so we fetch each once.
  const individualPromptIdByUuid = {}
  fields.forEach((field) => {
    if (
      field.field_type === 'extraction' &&
      field.prompt_uuid &&
      field.prompt_id &&
      field.prompt_uuid !== sharedPrompt?.prompt_uuid
    ) {
      individualPromptIdByUuid[field.prompt_uuid] = field.prompt_id
    }
  })

  await Promise.all(
    Object.entries(individualPromptIdByUuid).map(async ([uuid, promptId]) => {
      try {
        fieldPrompts[uuid] = await fetchingAPI(
          `${apiService.summary}/prompts/${promptId}`,
          'GET',
          dispatch
        )
      } catch (err) {
        // A prompt we can't load just won't surface a model on its field.
        console.error(err)
      }
    })
  )

  return fieldPrompts
}

// NumberField emits a string (or '' when cleared); saved prompt records store a
// number. Normalize both so an unchanged token value doesn't read as a diff.
const normalizeMaxTokens = (value) => (isNil(value) || value === '' ? null : Number(value))

// The snake_case patch spread onto a POST /prompts body from the model
// selector's prompt editor. Each field is only included when the caller actually
// provided it, so a change to one thing never clobbers the others: the model is
// carried only when an id is chosen (a null/absent id is a fallback, not a
// change), and each prompt-text/token field only when defined ("edited here").
//
// `llm_model_id` is the only model signal sent — see `withoutLegacyModel`, which
// drops the legacy `model` name off the body the saved record is spread from.
const buildPromptOverrides = ({ llmModelId, systemPrompt, userPrompt, maxResponseTokens }) => {
  const overrides = {}
  if (llmModelId) {
    overrides.llm_model_id = llmModelId
  }
  if (systemPrompt !== undefined) {
    overrides.system_prompt = systemPrompt
  }
  if (userPrompt !== undefined) {
    overrides.user_prompt = userPrompt
  }
  if (maxResponseTokens !== undefined) {
    overrides.max_response_tokens = normalizeMaxTokens(maxResponseTokens)
  }
  return overrides
}

// Whether the overrides differ from the saved prompt record — used to gate
// versioning a new prompt on save. Only compares the fields the overrides
// actually carry, so a model-only change isn't read as a prompt-text change
// (and a text-only change on a field with no model id still counts).
const promptOverridesChanged = (overrides, savedPrompt) =>
  ('llm_model_id' in overrides && overrides.llm_model_id !== savedPrompt.llm_model_id) ||
  ('system_prompt' in overrides &&
    (overrides.system_prompt ?? '') !== (savedPrompt.system_prompt ?? '')) ||
  ('user_prompt' in overrides &&
    (overrides.user_prompt ?? '') !== (savedPrompt.user_prompt ?? '')) ||
  ('max_response_tokens' in overrides &&
    overrides.max_response_tokens !== normalizeMaxTokens(savedPrompt.max_response_tokens))

// The unsaved prompt-editor edits to send as a test override — only the
// system/user prompt and max-token fields that differ from the saved record
// (`edited`/`saved` both snake_case). A field absent from `edited` (undefined)
// is "not edited here" and is skipped, so an unchanged prompt isn't resent.
const buildChangedPromptContent = (edited = {}, saved = {}) => {
  const override = {}
  if (
    edited.system_prompt !== undefined &&
    (edited.system_prompt ?? '') !== (saved.system_prompt ?? '')
  ) {
    override.system_prompt = edited.system_prompt
  }
  if (
    edited.user_prompt !== undefined &&
    (edited.user_prompt ?? '') !== (saved.user_prompt ?? '')
  ) {
    override.user_prompt = edited.user_prompt
  }
  if (edited.max_response_tokens !== undefined) {
    const editedTokens = normalizeMaxTokens(edited.max_response_tokens)
    if (editedTokens !== normalizeMaxTokens(saved.max_response_tokens)) {
      override.max_response_tokens = editedTokens
    }
  }
  return override
}

// Creates a brand-new taxonomy from a flat option list — the backend mints the
// uuid when none is sent — and returns the assigned taxonomy_uuid. Used when
// saving a newly added pick_list/categorization field, which has no taxonomy
// yet. (saveTaxonomyOptions instead OVERWRITES an existing taxonomy by uuid.)
const createTaxonomy = async (dispatch, { name, options, organizationId }) => {
  const saved = await fetchingAPI(
    `${apiService.summary}/taxonomies`,
    'POST',
    dispatch,
    JSON.stringify({
      taxonomy_uuid: null,
      name: name.trim(),
      description: '',
      requested_organization_id: organizationId,
      sub_categories: options.map((option) => ({
        taxonomy_uuid: null,
        name: option.name.trim(),
        description: option.description ?? '',
        sub_categories: [],
      })),
    })
  )
  // Fail loudly rather than silently linking the field to no taxonomy: a
  // pick_list/categorization field without a taxonomy_uuid is invalid, and the
  // caller's try/catch surfaces the error instead of saving a broken field.
  if (!saved?.taxonomy_uuid) {
    throw new Error(`Taxonomy creation for field "${name}" returned no taxonomy_uuid`)
  }
  // The assigned category uuids come back with the saved tree. A gate on a
  // brand-new selection field is authored against option NAMES — nothing else
  // exists yet — so the save has to map those onto the uuids minted here before
  // it can send the edges.
  const optionUuidsByName = {}
  ;(saved.sub_categories ?? []).forEach((category) => {
    if (category?.name && category?.taxonomy_uuid) {
      optionUuidsByName[category.name] = category.taxonomy_uuid
    }
  })
  return { taxonomyUuid: saved.taxonomy_uuid, optionUuidsByName }
}

// Put a flow on screen in the playbook editor: the flow itself, plus the prompt
// records its extraction fields run on (admins only — they're the ones who see
// the model selector). Shared by the initial load and by switching to an older
// version, so both paths render a flow the same way.
//
// Callers own loading.prompt: the editor re-seeds its rows from rtnBitFlow on
// mount, so it has to stay unmounted until the field prompts land too, or the
// per-field models come up empty.
const loadFlowIntoEditor = async (dispatch, getState, flow) => {
  const { organizationid } = getState().currentUser

  dispatch(actions.setRTNBitFlow(flow))

  // An existing flow with no fields is a valid bit-flow state — the UI offers
  // to generate starting fields.
  if (isEmpty(flow.fields)) {
    dispatch(actions.setRTNPrompt(''))
    return
  }

  // Admins see the model selector, so pull in the actual prompt record backing
  // this flow's extraction fields (see getExtractionPromptId — pick_list fields
  // carry their own prompt and must not be loaded here).
  //
  // On an older version this is the prompt that version ran on: the history
  // endpoint resolves each field's prompt_id to the newest prompt for its
  // prompt_uuid saved at or before the version was cut (the service's
  // get_flow_versions). The editor shows it locked; restoring the version puts it
  // back (see saveBitFlowForPlaybook).
  if (organizationid === 1) {
    const extractionPromptId = getExtractionPromptId(flow.fields)
    if (extractionPromptId) {
      try {
        const prompt = await fetchingAPI(
          `${apiService.summary}/prompts/${extractionPromptId}`,
          'GET',
          dispatch
        )
        dispatch(actions.setRTNPrompt(prompt))

        // The shared prompt drives the main model dropdown; any extraction
        // field on a different prompt is "individual execution" and shows its
        // own model. Load those prompt records too.
        const fieldPrompts = await loadExtractionFieldPrompts(dispatch, flow.fields, prompt)
        dispatch(actions.setRTNFieldPrompts(fieldPrompts))

        await loadModelOptions(dispatch, getState)
        return
      } catch (err) {
        // Fall through to the synthesized prompt fallback below
      }
    }
  }

  const promptFields = convertBitFlowFieldsToPromptFields(flow.fields)
  const userPrompt = buildPrompt(promptFields)
  dispatch(actions.setRTNPrompt({ user_prompt: userPrompt, model: 'gpt-4o-mini' }))
  await loadModelOptions(dispatch, getState)
}

// A flow's versions, newest first — element 0 is the active one. Every save cuts
// a version sharing the flow_uuid, so this is the configuration's audit trail.
// The org default flow is just the flow with no playbook or call-type scope, so
// both editors read their history here.
const getFlowVersions = (dispatch, flowUuid) =>
  fetchingAPI(`${apiService.summary}/bit-flows/${flowUuid}/versions`, 'GET', dispatch)

// The loaded playbook flow's history, so the editor can offer a read-only view
// of an older version. A failure raises no toast — without a history the version
// dropdown simply doesn't render, and the active flow is still fully editable —
// but it's logged, since a history that never loads is otherwise invisible.
export const fetchBitFlowVersions = (flowUuid) => async (dispatch) => {
  dispatch(actions.setLoading({ rtnBitFlowVersions: true }))

  try {
    const versions = await getFlowVersions(dispatch, flowUuid)
    dispatch(actions.setRTNBitFlowVersions(versions))
  } catch (err) {
    console.error(err)
    dispatch(actions.setRTNBitFlowVersions([]))
  } finally {
    dispatch(actions.setLoading({ rtnBitFlowVersions: false }))
  }
}

// The org default flow's history — same endpoint, its own slice, since the
// defaults editor stays mounted alongside the playbook one and the two can't
// share a list. Handles failure the same way as above.
export const fetchOrgDefaultFlowVersions = (flowUuid) => async (dispatch) => {
  dispatch(actions.setLoading({ rtnOrgDefaultFlowVersions: true }))

  try {
    const versions = await getFlowVersions(dispatch, flowUuid)
    dispatch(actions.setRTNOrgDefaultFlowVersions(versions))
  } catch (err) {
    console.error(err)
    dispatch(actions.setRTNOrgDefaultFlowVersions([]))
  } finally {
    dispatch(actions.setLoading({ rtnOrgDefaultFlowVersions: false }))
  }
}

// Show one of the loaded flow's older versions. The version already carries its
// fields and edges from the history fetch, so this only swaps what's on screen —
// nothing is saved, and the editor locks itself on a non-active flow (see
// PromptForm).
export const loadBitFlowVersion = (flowId) => async (dispatch, getState) => {
  const { rtnBitFlowVersions } = getState().playbook
  const version = rtnBitFlowVersions.find((candidate) => candidate.id === flowId)
  if (!version) {
    return
  }

  dispatch(actions.setLoading({ prompt: true }))
  dispatch(actions.setRTNBitFlow(null))
  dispatch(actions.setRTNFieldPrompts({}))

  try {
    await loadFlowIntoEditor(dispatch, getState, version)
  } catch (err) {
    console.error(err)
    toast.error('Failed to load this version', { cause: err })
  } finally {
    dispatch(actions.setLoading({ prompt: false }))
  }
}

export const getSummaryConfigForPlaybook = (id, callType) => {
  return async (dispatch, getState) => {
    dispatch(actions.setLoading({ prompt: true }))
    dispatch(actions.setRTNBitFlow(null))
    dispatch(actions.setRTNBitFlowVersions([]))
    dispatch(actions.setRTNFieldPrompts({}))

    try {
      const resolvedCallType = callType || 'call'
      const { rtnOrg } = getState().playbook
      const { organizationid } = getState().currentUser
      const orgId = rtnOrg || organizationid

      // Try loading bit flow fields first. The backend supports filtering by
      // playbook_cid + call_type, so narrow the query rather than fetching
      // every flow for the org.
      const params = {
        requested_organization_id: orgId,
        call_type: resolvedCallType,
      }
      if (id) {
        params.playbook_cid = id
      }
      const flows = await fetchingAPI(
        `${apiService.summary}/bit-flows?${queryString.stringify(params)}`,
        'GET',
        dispatch
      )

      // For call + playbook the backend returns at most one matching flow.
      // For non-call channels we omit playbook_cid so the backend returns all
      // flows for (org, call_type); pick the one with no playbook scope.
      const matchingFlow = id ? flows[0] : flows.find((flow) => flow.playbook_cid === null)

      if (matchingFlow) {
        await loadFlowIntoEditor(dispatch, getState, matchingFlow)
        // The history is keyed on flow_uuid, and isn't needed to render the
        // editor — fetch it alongside rather than holding the load open.
        dispatch(fetchBitFlowVersions(matchingFlow.flow_uuid))
        return
      }

      // Notetaker is bit-flow only — there's no summary-config fallback. With no
      // matching flow there's nothing to load, so surface the same error toast
      // we show on any other load failure.
      throw new Error(`No bit flow found for playbook ${id || ''} on ${resolvedCallType}`)
    } catch (err) {
      console.error(err)
      toast.error('Failed to load prompt for playbook', { cause: err })
    } finally {
      dispatch(actions.setLoading({ prompt: false }))
    }
  }
}

export const saveBitFlowForPlaybook =
  ({
    playbookCid,
    promptFields,
    callType,
    organizationId,
    flowUuid,
    llmModelId,
    systemPrompt,
    userPrompt,
    maxResponseTokens,
    fieldLinks = [],
    branchingEnabled = false,
    forceNewVersion = false,
  }) =>
  async (dispatch, getState) => {
    dispatch(actions.setLoading({ savePlaybook: true }))

    // Set by the flow POST below if it rejects. Only that request's message is
    // the backend telling an admin what's wrong with their flow; everything else
    // this try awaits is an internal step.
    let flowSaveRejection = null

    try {
      const { organizations, currentOrganization } = getState()
      const { organizationid: currentUserOrgId } = getState().currentUser
      const { rtnPrompt, rtnBitFlow, rtnFieldPrompts } = getState().playbook
      const org = organizations.find(
        (organization) =>
          organization.organizationid === organizationId || organization.id === organizationId
      )
      const showToUser = (org || currentOrganization)?.show_summarization_notes ?? false

      // Preserve each existing field's server-owned attributes on save (matched
      // by field_id, which survives rename/reorder) so editing a pick_list field
      // doesn't rewrite it as an extraction field or drop its taxonomy. Brand-new
      // fields have no field_id and default to a string extraction field.
      const savedFieldByFieldId = {}
      if (rtnBitFlow?.fields) {
        rtnBitFlow.fields.forEach((field) => {
          savedFieldByFieldId[field.field_id] = field
        })
      }

      // New extraction fields added in the UI have no prompt_uuid yet. Link
      // them to the loaded extraction prompt so they share it instead of
      // compiling a new one (and never borrow a pick_list field's prompt): the
      // loaded prompt record (rtnPrompt) for admins, otherwise the uuid shared
      // by the flow's existing extraction fields. A brand-new flow has no
      // extraction prompt yet — leave it for the backend to auto-compile.
      const extractionPromptUuid =
        rtnPrompt?.prompt_uuid ||
        rtnBitFlow?.fields?.find((field) => field.field_type === 'extraction' && field.prompt_uuid)
          ?.prompt_uuid

      // Admins can change the model. If they did, POST a new version of the
      // EXTRACTION prompt (the one loaded into rtnPrompt — its prompt_uuid, not
      // an arbitrary field's, so a pick_list field's prompt is never versioned
      // here). The bit flow links by prompt_uuid, so it auto-picks up the new
      // version on the next read — no need to touch the bit flow record for a
      // model-only change.
      const isAdmin = currentUserOrgId === 1
      // Fields link to prompts by prompt_uuid, so a flow always runs the newest
      // prompt for each. An older version on screen shows the prompt it ran on
      // instead (see loadFlowIntoEditor), so restoring it saves each prompt record
      // it loaded again, even though nothing on screen was edited and the diffs
      // below see no change. Unconditionally: nothing here reliably knows which
      // prompt is newest (a prompt-only save versions one without refreshing the
      // history), and an identical extra prompt version costs nothing.
      const isRestoringPrompt = (prompt) => forceNewVersion && !isNil(prompt?.id)
      // The prompt versions we mint here, keyed by prompt_uuid. Used to refresh
      // the in-memory maps from the actual POST responses instead of re-GETting
      // by the flow's now-stale prompt_ids (which would fetch the old version).
      const versionedFieldPrompts = {}
      const sharedOverrides = buildPromptOverrides({
        llmModelId,
        systemPrompt,
        userPrompt,
        maxResponseTokens,
      })
      // The shared prompt as it stands after this save. When the shared model or
      // text changed here, this — not the stale pre-save rtnPrompt — is what the
      // backend copies into any field promoted in the same save, so promoted-field
      // re-versioning must diff against it.
      const postSaveSharedPrompt = { ...rtnPrompt, ...sharedOverrides }
      const sharedPromptChanged =
        isAdmin &&
        rtnPrompt?.prompt_uuid &&
        (promptOverridesChanged(sharedOverrides, rtnPrompt) || isRestoringPrompt(rtnPrompt))

      if (sharedPromptChanged) {
        const versioned = await fetchingAPI(
          `${apiService.summary}/prompts`,
          'POST',
          dispatch,
          JSON.stringify(
            withoutLegacyModel({
              ...rtnPrompt,
              ...sharedOverrides,
              organization_id: organizationId,
            })
          )
        )
        versionedFieldPrompts[rtnPrompt.prompt_uuid] = versioned
        // Keep the stored prompt current so a later save in the same session
        // diffs against the version we just wrote, not the stale one.
        dispatch(actions.setRTNPrompt(postSaveSharedPrompt))
      }

      // Individual-execution fields run on their own prompt. Version it when an
      // admin edits its model, system/user prompt, or max tokens (keyed by
      // prompt_uuid, deduped so a prompt shared by several individual fields is
      // versioned once). Not gated on llm_model_id: it can be a null fallback,
      // and prompt-text edits must still be saved. llm_model_id is seeded from the
      // field's own prompt, so it's a no-op unless actually changed.
      // The flow links by prompt_uuid and picks up the new version on next read.
      if (isAdmin) {
        const individualOverridesByUuid = {}
        promptFields.forEach((field) => {
          if (
            field.individualExecution &&
            field.prompt_uuid &&
            field.prompt_uuid !== extractionPromptUuid
          ) {
            individualOverridesByUuid[field.prompt_uuid] = buildPromptOverrides({
              llmModelId: field.llm_model_id,
              systemPrompt: field.system_prompt,
              userPrompt: field.user_prompt,
              maxResponseTokens: field.max_response_tokens,
            })
          }
        })

        await Promise.all(
          Object.entries(individualOverridesByUuid).map(async ([promptUuid, overrides]) => {
            const ownPrompt = rtnFieldPrompts?.[promptUuid]
            if (
              !ownPrompt ||
              !(promptOverridesChanged(overrides, ownPrompt) || isRestoringPrompt(ownPrompt))
            ) {
              return
            }
            const versioned = await fetchingAPI(
              `${apiService.summary}/prompts`,
              'POST',
              dispatch,
              JSON.stringify(
                withoutLegacyModel({
                  ...ownPrompt,
                  ...overrides,
                  organization_id: organizationId,
                })
              )
            )
            versionedFieldPrompts[promptUuid] = versioned
          })
        )
      }

      // Only POST the bit flow when the field set actually changed (rename,
      // edit, reorder, add, remove, visibility toggle). A model-only change is
      // captured by the new prompt version above; the existing bit flow keeps
      // pointing at the same prompt_uuid and picks up the new version on next
      // read.
      const originalFields = rtnBitFlow?.fields
        ? [...rtnBitFlow.fields].sort(
            (fieldA, fieldB) => (fieldA.order_id ?? 0) - (fieldB.order_id ?? 0)
          )
        : null
      // A field loaded as "individual" when no other extraction field shared its
      // prompt. Mirror that detection here (by prompt-usage count) so toggling
      // standalone on/off — which changes nothing else on the row — still
      // triggers the flow POST that carries the grouping change to the backend.
      const originalPromptCounts = {}
      if (originalFields) {
        originalFields.forEach((field) => {
          if (field.field_type === 'extraction' && field.prompt_uuid) {
            originalPromptCounts[field.prompt_uuid] =
              (originalPromptCounts[field.prompt_uuid] ?? 0) + 1
          }
        })
      }
      const fieldsChanged =
        !originalFields ||
        originalFields.length !== promptFields.length ||
        promptFields.some((field, index) => {
          const original = originalFields[index]
          // Rows that never carried the flag (legacy seeds) can't change it
          const showToUserChanged =
            !isNil(field.show_to_user) && field.show_to_user !== (original?.show_to_user ?? false)
          const originalIndividual =
            original?.field_type === 'extraction' &&
            !!original?.prompt_uuid &&
            originalPromptCounts[original.prompt_uuid] === 1
          // Only admins can change grouping, so only they can trip this.
          const standaloneChanged =
            isAdmin && (field.individualExecution === true) !== originalIndividual
          // Which taxonomy a field resolves against is part of the field, and
          // changing only that leaves the rest of the row alone: forking an
          // inherited taxonomy drops the uuid for a copy minted below, and
          // linking back to the org default replaces it. Without this the guard
          // swallows both.
          const taxonomyChanged =
            (original?.taxonomy_uuid ?? null) !== (field.taxonomy_uuid ?? null)
          return (
            original?.field_name?.trim() !== field.key.trim() ||
            (original?.description ?? '') !== (field.value ?? '') ||
            showToUserChanged ||
            standaloneChanged ||
            taxonomyChanged
          )
        })

      // Conditions live on the flow's edges, not on any field, so a save that
      // only adds or edits one leaves every field byte-identical. Without this
      // the guard below swallows it and reports success having sent nothing.
      // A description's `$token` is an edge too — the service reads its prompt
      // inputs off the incoming edges and never scans the text — so the links
      // being saved are the conditions the editor holds plus the ones its
      // descriptions imply. Both sides of the comparison use the same list, or
      // editing only a token would look like no change at all.
      //
      // Behind the flag: with it off the editor shows nothing about tokens, so
      // deriving edges from prose would have the feature running invisibly.
      // What was already saved still round-trips untouched.
      const linksToSave = branchingEnabled ? withTokenLinks(promptFields, fieldLinks) : fieldLinks
      const linksChanged = haveLinksChanged(
        linksToSave,
        seedFieldLinks(rtnBitFlow?.edges ?? [], promptFields)
      )

      // Restoring an older version re-saves fields identical to the flow on
      // screen (that version), so the no-op guard below would swallow the very
      // save that's wanted — cutting a new active version from those fields.
      if (!fieldsChanged && !linksChanged && !forceNewVersion) {
        // A prompt-only change (model, system/user prompt, or max tokens) versioned
        // one or more prompts above. Reflect those from the POST responses we just
        // got — re-GETting by the loaded flow's prompt_ids would fetch the old
        // versions they still point at. Re-emit the (unchanged) flow so the editor
        // re-seeds its unsaved-changes baseline from the fresh prompts;
        // otherwise the per-field edit still reads as unsaved even though it
        // was just written.
        if (isAdmin && rtnPrompt?.prompt_uuid && !isEmpty(versionedFieldPrompts)) {
          dispatch(actions.setRTNFieldPrompts({ ...rtnFieldPrompts, ...versionedFieldPrompts }))
          dispatch(actions.setRTNBitFlow({ ...rtnBitFlow }))
        }
        toast.success('Prompt updated successfully')
        return
      }

      // Option-backed fields that carry their options inline but have no taxonomy
      // yet: a brand-new field added via the guided modal, or an inherited field
      // whose options were forked off the org default. Mint one per field (POST
      // /taxonomies with a null uuid) and map the returned uuid back by index so
      // the field mapping below can link the field to it. Staged options are what
      // marks a field as option-backed: pick_list/categorization always are, and a
      // voip_metadata field is when its Picklist data type was chosen.
      //
      // Deliberately broader than the test path's INLINE_TAXONOMY_FIELD_TYPES
      // allowlist, and the asymmetry is the point: any option-backed field needs a
      // real persisted taxonomy to reference, while only the selection types can
      // resolve an ad-hoc one at test time. A new option-backed type belongs here
      // automatically, and in that allowlist only if its executor reads options
      // off the request instead of the DB.
      const newTaxonomyUuidByIndex = {}
      const mintedOptionUuidsByFieldKey = {}
      await Promise.all(
        promptFields.map(async (field, index) => {
          // Keyed on the missing taxonomy rather than on the field being new, so
          // a fork — an existing field_id whose shared taxonomy the row dropped —
          // mints its own copy too. Mirrors saveOrgDefaultBitFlow.
          const needsTaxonomy =
            isNil(field.taxonomy_uuid) &&
            field.field_type !== 'extraction' &&
            !isEmpty(field.new_taxonomy_options)
          if (!needsTaxonomy) {
            return
          }
          const minted = await createTaxonomy(dispatch, {
            name: field.key,
            options: field.new_taxonomy_options,
            organizationId,
          })
          newTaxonomyUuidByIndex[index] = minted.taxonomyUuid
          mintedOptionUuidsByFieldKey[field.key] = minted.optionUuidsByName
        })
      )

      const fields = promptFields.map((field, index) => {
        const savedField = savedFieldByFieldId[field.field_id]
        // A saved field's server-owned type/taxonomy win; a brand-new field
        // (no saved record) falls back to what it was created with on the row,
        // so a pick_list/categorization field isn't forced back to extraction.
        const fieldType = savedField?.field_type || field.field_type || 'extraction'
        // The row's own taxonomy wins: it carries whatever the field resolves
        // against today, plus any deliberate change to that — a fork (uuid
        // dropped, so the copy minted above stands in) or a link back to the org
        // default's. The saved field is the fallback for a row that never carried
        // one, so editing a name or description can't strip a taxonomy.
        const taxonomyUuid =
          field.taxonomy_uuid || newTaxonomyUuidByIndex[index] || savedField?.taxonomy_uuid
        const baseField = {
          field_name: field.key.trim(),
          data_type: savedField?.data_type || field.data_type || 'string',
          field_type: fieldType,
          description: field.value,
          // Per-field agent visibility (call channel); fields that never carried
          // the flag fall back to the org-wide display setting.
          show_to_user: field.show_to_user ?? showToUser,
          order_id: index,
          // Preserve a pick_list field's taxonomy so editing its name/description
          // doesn't strip the option set it resolves against.
          ...(taxonomyUuid ? { taxonomy_uuid: taxonomyUuid } : {}),
        }

        // Non-extraction fields keep their own prompt (the backend builds a
        // selection prompt when there's none); grouping is extraction-only.
        if (fieldType !== 'extraction') {
          return { ...baseField, prompt_uuid: field.prompt_uuid }
        }

        // Individual execution is a Balto-admin-only concept, so non-admins
        // never alter grouping — keep a field's prompt, or share the main one.
        if (!isAdmin) {
          return { ...baseField, prompt_uuid: field.prompt_uuid || extractionPromptUuid }
        }

        // "On the shared prompt" = no prompt of its own, or the flow's main
        // extraction prompt. The backend's grouping/keep rules key off whether
        // a prompt_uuid is sent.
        const onSharedPrompt = !field.prompt_uuid || field.prompt_uuid === extractionPromptUuid

        if (field.individualExecution) {
          // Already standalone (owns a prompt nothing else uses) → keep it.
          // Newly promoted from the shared prompt → clear prompt_uuid and send a
          // unique new_grouping so the backend mints it a fresh, dedicated
          // prompt (index is unique per request, so each promoted field splits
          // out on its own rather than grouping together).
          return onSharedPrompt
            ? { ...baseField, new_grouping: index }
            : { ...baseField, prompt_uuid: field.prompt_uuid }
        }

        // Turned standalone off → re-merge onto the main shared prompt by
        // sending its uuid (a supplied prompt_uuid is honored as-is). Clearing
        // it instead would fall back to the field's prior standalone prompt.
        if (!onSharedPrompt) {
          return { ...baseField, prompt_uuid: extractionPromptUuid }
        }

        // Plain shared extraction field — keep it on the shared prompt.
        return { ...baseField, prompt_uuid: field.prompt_uuid || extractionPromptUuid }
      })

      const body = {
        requested_organization_id: organizationId,
        playbook_cid: playbookCid,
        call_type: callType || 'call',
        trigger_type: 'call_end',
        fields,
        // A gate on a brand-new selection field was authored against option
        // names; the taxonomies minted just above are what turn those into the
        // category uuids the service stores.
        edges: buildEdgePayload(
          resolveStagedOperands(linksToSave, promptFields, mintedOptionUuidsByFieldKey)
        ),
      }

      if (flowUuid) {
        body.flow_uuid = flowUuid
      }

      const savedFlow = await fetchingAPI(
        `${apiService.summary}/bit-flows`,
        'POST',
        dispatch,
        JSON.stringify(body),
        undefined,
        // The catch below is this request's only error surface, and it reports
        // the backend's own message. isSilent stops handleResponse toasting the
        // 500/unknown cases on top of that, which would double up.
        { isSilent: true }
      ).catch((err) => {
        // Remember which rejection this was. The try around it also awaits the
        // prompt-version POSTs, which are internal steps of "save the flow" —
        // their failures read as the generic line, and they are NOT silenced, so
        // re-labelling one here would both mis-describe it and duplicate the
        // toast handleResponse already showed.
        flowSaveRejection = err
        throw err
      })

      // A freshly promoted field is minted a copy of the shared prompt (shared
      // model). If the admin changed its model or prompt text/tokens, version its
      // new prompt now — otherwise the change would be lost. Match the promoted
      // form field to its minted prompt by name, reuse the shared prompt's
      // content, and keep the new records to seed the map below.
      const savedFieldByName = {}
      if (savedFlow.fields) {
        savedFlow.fields.forEach((field) => {
          savedFieldByName[field.field_name] = field
        })
      }
      if (isAdmin && rtnPrompt?.prompt_uuid) {
        await Promise.all(
          promptFields
            .filter((field) => {
              const wasPromoted =
                field.individualExecution &&
                (!field.prompt_uuid || field.prompt_uuid === extractionPromptUuid)
              if (!wasPromoted) {
                return false
              }
              // The mint copies the post-save shared prompt, so only version when
              // the field's chosen model/prompt/tokens differ from THAT (not the
              // stale pre-save rtnPrompt — the shared prompt may have changed in
              // this same save).
              return promptOverridesChanged(
                buildPromptOverrides({
                  llmModelId: field.llm_model_id,
                  systemPrompt: field.system_prompt,
                  userPrompt: field.user_prompt,
                  maxResponseTokens: field.max_response_tokens,
                }),
                postSaveSharedPrompt
              )
            })
            .map(async (field) => {
              // savedFlow returns fields keyed by the trimmed name we sent, so
              // match on the trimmed key too or a padded name would miss its
              // minted prompt and skip the override.
              const mintedPromptUuid = savedFieldByName[field.key.trim()]?.prompt_uuid
              if (!mintedPromptUuid) {
                return
              }
              const versioned = await fetchingAPI(
                `${apiService.summary}/prompts`,
                'POST',
                dispatch,
                JSON.stringify(
                  withoutLegacyModel({
                    ...postSaveSharedPrompt,
                    prompt_uuid: mintedPromptUuid,
                    ...buildPromptOverrides({
                      llmModelId: field.llm_model_id,
                      systemPrompt: field.system_prompt,
                      userPrompt: field.user_prompt,
                      maxResponseTokens: field.max_response_tokens,
                    }),
                    organization_id: organizationId,
                  })
                )
              )
              versionedFieldPrompts[mintedPromptUuid] = versioned
            })
        )
      }

      // Refresh the per-field prompt map from the persisted flow BEFORE swapping
      // in the saved flow, so the editor re-seeds (which keys off rtnBitFlow)
      // with each field's model — including a freshly minted standalone prompt —
      // in one pass instead of flashing empty. Only fetches the prompts that
      // diverge from the shared one, so flows without individual fields add no
      // requests. Layer the just-versioned prompts on top so a per-field model
      // change survives the refresh (which would otherwise read the minted
      // copy's shared model).
      if (isAdmin && rtnPrompt?.prompt_uuid) {
        const refreshed = await loadExtractionFieldPrompts(dispatch, savedFlow.fields, rtnPrompt)
        dispatch(actions.setRTNFieldPrompts({ ...refreshed, ...versionedFieldPrompts }))
      }
      dispatch(actions.setRTNBitFlow(savedFlow))
      // A rename/add persists new bit fields for the org, so refresh the
      // reuse list — otherwise the new name won't show as a suggestion on
      // other playbooks until the page reloads.
      dispatch(fetchOrgFieldNames(organizationId))
      // The save cut a new version — refresh the history so the dropdown offers
      // it (and the one it just superseded).
      dispatch(fetchBitFlowVersions(savedFlow.flow_uuid))
      toast.success('Prompt updated successfully')
    } catch (err) {
      console.error(err)
      // Prefer the backend's own message, but only for the flow save. It rejects
      // its validation failures (400/409/422) with a reason that names the field
      // and what's wrong with it — a conflicting data_type, a categorization
      // taxonomy with no category descriptions, a name reserved by an org's
      // post-processing. handleResponse leaves surfacing those to the caller, so
      // replacing them with a fixed string dropped every one of them on the
      // floor: the admin got "failed", at the end of an edit, with nothing
      // saying which field. apiErrorMessage keeps the generic line for the
      // failures whose text isn't user-facing — a local throw in this action, a
      // network error.
      const fallback = 'Failed to save prompt for playbook'
      toast.error(err === flowSaveRejection ? apiErrorMessage(err, fallback) : fallback)
    } finally {
      dispatch(actions.setLoading({ savePlaybook: false }))
    }
  }

// How the backend resolves playbook flows that inherit a default field being
// removed: `keep` freezes the default's values onto each as a standalone
// override; `delete` removes the field from those flows too. Mirrors the
// summary-service RemovedDefaultFieldStrategy enum.
export const REMOVED_DEFAULT_FIELD_STRATEGY = {
  KEEP: 'keep',
  DELETE: 'delete',
}

// The org default flow is the org's bit flow with no playbook or call-type
// scope — its field descriptions cascade to every flow that inherits them.
// `default_only=true` returns just that flow (or nothing), so we don't have to
// fetch and sift the whole org. Errors stay silent: the org defaults section
// simply doesn't render when there's nothing to show.
export const fetchOrgDefaultBitFlow = (organizationId) => async (dispatch, getState) => {
  dispatch(actions.setLoading({ orgDefaultFlow: true }))
  dispatch(actions.setRTNOrgDefaultFlow(null))
  dispatch(actions.setRTNOrgDefaultFlowVersions([]))
  dispatch(actions.setRTNOrgDefaultViewedVersion(null))
  dispatch(actions.setRTNOrgDefaultPrompt(null))
  dispatch(actions.setRTNOrgDefaultRemovalPreview(null))

  try {
    const params = { requested_organization_id: organizationId, default_only: true }
    const flows = await fetchingAPI(
      `${apiService.summary}/bit-flows?${queryString.stringify(params)}`,
      'GET',
      dispatch
    )
    const defaultFlow = flows[0] || null
    dispatch(actions.setRTNOrgDefaultFlow(defaultFlow))

    // Admins can edit the default flow's model, so pull in the prompt record
    // backing its extraction fields — same approach as the playbook path in
    // getSummaryConfigForPlaybook.
    const { organizationid } = getState().currentUser
    if (defaultFlow && organizationid === 1) {
      // Extraction fields only — a pick_list or categorization default carries
      // its own selection prompt, and the model selector edits the shared
      // extraction prompt (see getExtractionPromptId).
      const extractionPromptId = getExtractionPromptId(defaultFlow.fields)
      if (extractionPromptId) {
        try {
          const prompt = await fetchingAPI(
            `${apiService.summary}/prompts/${extractionPromptId}`,
            'GET',
            dispatch
          )
          dispatch(actions.setRTNOrgDefaultPrompt(prompt))
        } catch (err) {
          // Without the prompt record the model selector just won't preselect
        }
      }
      await loadModelOptions(dispatch, getState)
    }

    // The history isn't needed to render the editor — fetch it last rather than
    // holding the load open.
    if (defaultFlow) {
      dispatch(fetchOrgDefaultFlowVersions(defaultFlow.flow_uuid))
    }
  } catch (err) {
    console.error(err)
  } finally {
    dispatch(actions.setLoading({ orgDefaultFlow: false }))
  }
}

// Put an older org-default version on screen (null goes back to the current
// one). The version carries its own fields; admins also see the prompt behind
// them, so load the one this version ran on — the history resolves its
// prompt_id to that (see loadFlowIntoEditor). Until it lands the editor shows
// the current prompt and holds the restore; if it can't be read, the restore
// stays held (see OrgDefaultsForm).
//
// Each call supersedes the last, so a slow response for a version that's no
// longer on screen is dropped rather than shown against another one.
let latestOrgDefaultViewRequest = 0
export const viewOrgDefaultFlowVersion = (version) => async (dispatch, getState) => {
  latestOrgDefaultViewRequest += 1
  const request = latestOrgDefaultViewRequest
  const isLatestRequest = () => request === latestOrgDefaultViewRequest

  dispatch(actions.setRTNOrgDefaultViewedVersion(version))

  const { organizationid } = getState().currentUser
  const promptId = version && organizationid === 1 ? getExtractionPromptId(version.fields) : null
  dispatch(actions.setLoading({ rtnOrgDefaultViewedPrompt: Boolean(promptId) }))
  if (!promptId) {
    return
  }

  try {
    const prompt = await fetchingAPI(`${apiService.summary}/prompts/${promptId}`, 'GET', dispatch)
    if (isLatestRequest()) {
      dispatch(actions.setRTNOrgDefaultViewedPrompt(prompt))
    }
  } catch (err) {
    console.error(err)
    if (isLatestRequest()) {
      dispatch(actions.setRTNOrgDefaultViewedPromptFailed(true))
    }
  } finally {
    if (isLatestRequest()) {
      dispatch(actions.setLoading({ rtnOrgDefaultViewedPrompt: false }))
    }
  }
}

export const saveOrgDefaultBitFlow =
  ({
    organizationId,
    promptFields,
    llmModelId,
    systemPrompt,
    userPrompt,
    maxResponseTokens,
    removedFieldStrategy,
  }) =>
  async (dispatch, getState) => {
    dispatch(actions.setLoading({ saveOrgDefaultFlow: true }))
    // Taxonomies minted for newly added option-backed fields, by row index.
    // Returned to the caller so a save the backend bounces for a removal
    // decision can be retried against the same taxonomies (see below).
    const mintedTaxonomyUuidByIndex = {}
    // See saveBitFlowForPlaybook: only the flow POST's message is the backend
    // telling an admin what's wrong with their flow.
    let flowSaveRejection = null

    try {
      const { organizations, currentOrganization } = getState()
      const { organizationid: currentUserOrgId } = getState().currentUser
      const {
        rtnOrgDefaultFlow,
        rtnOrgDefaultPrompt,
        rtnOrgDefaultViewedVersion,
        rtnOrgDefaultViewedPrompt,
      } = getState().playbook
      const org = organizations.find(
        (organization) =>
          organization.organizationid === organizationId || organization.id === organizationId
      )
      const showToUser = (org || currentOrganization)?.show_summarization_notes ?? false

      // Saved rows carry their stored config (type, prompt, taxonomy, display)
      // through the form, so preserve it. A new extraction field has no prompt
      // yet and shares an extraction sibling's — never a selection field's,
      // which runs a prompt built for its options.
      const fallbackPromptUuid = promptFields.find(
        (field) => (field.field_type || 'extraction') === 'extraction' && field.prompt_uuid
      )?.prompt_uuid

      // Admins can change the model. If they did, POST a new version of the
      // existing prompt (same prompt_uuid) — the flow links by prompt_uuid,
      // so it picks up the new version without a flow POST. Mirrors
      // saveBitFlowForPlaybook.
      const isAdmin = currentUserOrgId === 1
      const overrides = buildPromptOverrides({
        llmModelId,
        systemPrompt,
        userPrompt,
        maxResponseTokens,
      })
      // Restoring an older version puts back the whole prompt record it ran on,
      // not just the fields the editor shows, so it builds on that record. It's
      // still diffed against the current prompt — that's what it replaces — and
      // re-saved even when the editable fields match, since the rest may not.
      const restoringPrompt =
        !isNil(rtnOrgDefaultViewedVersion) && !isNil(rtnOrgDefaultViewedPrompt)
      const basePrompt = restoringPrompt ? rtnOrgDefaultViewedPrompt : rtnOrgDefaultPrompt
      const promptChanged =
        isAdmin &&
        fallbackPromptUuid &&
        rtnOrgDefaultPrompt?.prompt_uuid &&
        (restoringPrompt || promptOverridesChanged(overrides, rtnOrgDefaultPrompt))

      if (promptChanged) {
        await fetchingAPI(
          `${apiService.summary}/prompts`,
          'POST',
          dispatch,
          JSON.stringify(
            withoutLegacyModel({
              ...basePrompt,
              ...overrides,
              prompt_uuid: fallbackPromptUuid,
              organization_id: organizationId,
            })
          )
        )
        // Keep the stored prompt current so the next save diffs correctly
        dispatch(actions.setRTNOrgDefaultPrompt({ ...basePrompt, ...overrides }))
      }

      // Only POST the flow when the field set actually changed — a
      // model-only change is fully captured by the new prompt version above.
      const originalFields = [...rtnOrgDefaultFlow.fields].sort(
        (fieldA, fieldB) => (fieldA.order_id ?? 0) - (fieldB.order_id ?? 0)
      )
      const fieldsChanged =
        originalFields.length !== promptFields.length ||
        promptFields.some((field, index) => {
          const original = originalFields[index]
          // Rows that never carried the flag can't change it
          const showToUserChanged =
            !isNil(field.show_to_user) && field.show_to_user !== (original?.show_to_user ?? false)
          return (
            original?.field_name?.trim() !== field.key.trim() ||
            (original?.description ?? '') !== (field.value ?? '') ||
            showToUserChanged
          )
        })

      if (!fieldsChanged) {
        // Restoring a version whose fields match the current ones has nothing to
        // save, but the editor should still come back to the current version
        // rather than sit on the read-only banner.
        dispatch(actions.setRTNOrgDefaultViewedVersion(null))
        toast.success('Organization defaults updated successfully')
        return { mintedTaxonomyUuidByIndex }
      }

      // A field added through the guided modal carries its options inline but has
      // no taxonomy yet. Mint one per field and map the uuid back by index so the
      // mapping below links the field to it — as saveBitFlowForPlaybook does.
      //
      // Keyed on the taxonomy rather than on the field being new (the playbook
      // path's isNil(field_id) && field_type !== 'extraction'), because this
      // editor can be asked to save the same rows twice: a removal decision
      // bounces the first attempt, and its keep/delete retry re-submits rows the
      // editor has since linked to the taxonomies that attempt minted. Skipping
      // any row that already has one covers both a saved field and that retry;
      // minting again would orphan the first.
      await Promise.all(
        promptFields.map(async (field, index) => {
          if (!isNil(field.taxonomy_uuid) || isEmpty(field.new_taxonomy_options)) {
            return
          }
          const { taxonomyUuid } = await createTaxonomy(dispatch, {
            name: field.key,
            options: field.new_taxonomy_options,
            organizationId,
          })
          mintedTaxonomyUuidByIndex[index] = taxonomyUuid
        })
      )

      const fields = promptFields.map((field, index) => {
        const fieldType = field.field_type || 'extraction'
        return {
          field_name: field.key.trim(),
          data_type: field.data_type || 'string',
          field_type: fieldType,
          description: field.value,
          // Only extraction fields share the flow's extraction prompt: a
          // selection field runs one the backend builds from its options, and a
          // voip_metadata field runs none at all.
          prompt_uuid:
            fieldType === 'extraction'
              ? field.prompt_uuid || fallbackPromptUuid
              : field.prompt_uuid,
          taxonomy_uuid: field.taxonomy_uuid || mintedTaxonomyUuidByIndex[index] || null,
          compare_value: field.compare_value || null,
          show_to_user: field.show_to_user ?? showToUser,
          order_id: index,
        }
      })

      const body = {
        requested_organization_id: organizationId,
        playbook_cid: null,
        call_type: null,
        trigger_type: 'call_end',
        flow_uuid: rtnOrgDefaultFlow.flow_uuid,
        fields,
        edges: rtnOrgDefaultFlow.edges,
      }

      // Removing a default field that playbooks inherit can't go through
      // blindly — the strategy (keep | delete) tells the backend how to resolve
      // those playbooks. Reconciliation "could be removed" notes are advisory
      // and never block, so no bypass flag is needed.
      const strategyParam = removedFieldStrategy
        ? `?${queryString.stringify({ removed_default_field_strategy: removedFieldStrategy })}`
        : ''
      const savedFlow = await fetchingAPI(
        `${apiService.summary}/bit-flows${strategyParam}`,
        'POST',
        dispatch,
        JSON.stringify(body),
        undefined,
        // See the playbook flow save above: the catch owns this request's error
        // presentation, so handleResponse mustn't toast on top of it.
        { isSilent: true }
      ).catch((err) => {
        // Likewise — the taxonomy POSTs this try also awaits are not silenced,
        // so only this rejection may be re-labelled.
        flowSaveRejection = err
        throw err
      })

      // id 0 is the backend's non-persisted response: nothing was saved. The
      // expected case is the removal preview (a removed default field other
      // playbooks inherit), which carries warnings naming those flows — surface
      // them so the user picks keep or delete, then we retry with a strategy.
      // Guard the whole id-0 case so we never store a non-persisted flow even
      // if warnings somehow come back empty.
      if (savedFlow.id === 0) {
        if (!isEmpty(savedFlow.warnings)) {
          dispatch(actions.setRTNOrgDefaultRemovalPreview(savedFlow.warnings))
        } else {
          toast.error('Failed to save organization defaults')
        }
        return { mintedTaxonomyUuidByIndex }
      }

      dispatch(actions.setRTNOrgDefaultRemovalPreview(null))
      dispatch(actions.setRTNOrgDefaultFlow(savedFlow))
      // What was just saved is now the current version, so stop showing history
      // — this is how restoring an older version returns the editor to editable.
      dispatch(actions.setRTNOrgDefaultViewedVersion(null))
      toast.success('Organization defaults updated successfully')

      // A loaded playbook flow holds effective values resolved against the
      // old defaults — clear and reload it so inherited fields pick up the
      // new descriptions. Awaited so the save spinner covers the reload.
      const { rtnBitFlow } = getState().playbook
      if (rtnBitFlow) {
        dispatch(actions.setRTNPrompt(''))
        await dispatch(getSummaryConfigForPlaybook(rtnBitFlow.playbook_cid, rtnBitFlow.call_type))
      }

      // A rename/add persists new bit fields for the org, so refresh the reuse
      // list — otherwise the new name won't show as a suggestion on a playbook
      // until the page reloads.
      dispatch(fetchOrgFieldNames(organizationId))

      // The save cut a new version — refresh the history so the dropdown offers
      // it (and the one it just superseded).
      dispatch(fetchOrgDefaultFlowVersions(savedFlow.flow_uuid))
    } catch (err) {
      console.error(err)
      // Same as the playbook flow save: the backend's validation message names
      // the offending field, and swallowing it left the admin with nothing to
      // act on — but only that request's message, not the taxonomy POSTs'.
      const fallback = 'Failed to save organization defaults'
      toast.error(err === flowSaveRejection ? apiErrorMessage(err, fallback) : fallback)
    } finally {
      dispatch(actions.setLoading({ saveOrgDefaultFlow: false }))
    }
    return { mintedTaxonomyUuidByIndex }
  }

// Copies one field's saved description from the loaded (source) flow to other
// playbook flows sharing its call type — a list of targetPlaybookCids, or every
// such flow with copyToAllPlaybooks. Returns the normalized result the copy
// modal renders: which playbooks were updated, skipped (no flow/field for the
// call type), or errored. The API reports skips itself, so callers get one 200
// with all three buckets; genuine failures (e.g. an inaccessible playbook)
// reject and are surfaced by the caller.
export const copyFieldDescription =
  ({ sourceFlowUuid, fieldId, targetPlaybookCids, copyToAllPlaybooks }) =>
  async (dispatch) => {
    const body = { source_flow_uuid: sourceFlowUuid, field_id: fieldId }
    if (copyToAllPlaybooks) {
      body.copy_to_all_playbooks = true
    } else {
      body.target_playbook_cids = targetPlaybookCids
    }

    const response = await fetchingAPI(
      `${apiService.summary}/bit-flows/copy-field-description`,
      'POST',
      dispatch,
      JSON.stringify(body)
    )
    return {
      updated: response.updated_playbook_cids ?? [],
      skipped: response.skipped_playbook_cids ?? [],
      errored: response.errored_playbook_cids ?? [],
    }
  }

// The taxonomy endpoint returns a nested tree ({ taxonomy_uuid, name,
// description, sub_categories }). A pick_list field's selectable options are
// every descendant of the root (the root is just the container), deduped by
// name with the first occurrence kept — mirrors the service's
// TaxCat.pick_list_options. Each option keeps its taxonomy_uuid so a later save
// can reuse it. Returns the root meta plus that flat option list.
const extractTaxonomy = (taxonomy) => {
  const options = []
  const seenNames = {}
  const walk = (node) => {
    if (isEmpty(node?.sub_categories)) {
      return
    }
    node.sub_categories.forEach((child) => {
      if (!seenNames[child.name]) {
        seenNames[child.name] = true
        options.push({
          taxonomy_uuid: child.taxonomy_uuid,
          name: child.name,
          description: child.description,
        })
      }
      walk(child)
    })
  }
  walk(taxonomy)
  return {
    root: {
      taxonomy_uuid: taxonomy?.taxonomy_uuid,
      name: taxonomy?.name,
      description: taxonomy?.description,
    },
    options,
  }
}

// Loads a pick_list field's taxonomy by uuid and returns its root meta + flat,
// deduped option list for the options editor (admins only).
export const fetchTaxonomyOptions = (taxonomyUuid) => async (dispatch) => {
  const taxonomy = await fetchingAPI(
    `${apiService.summary}/taxonomies/${taxonomyUuid}`,
    'GET',
    dispatch
  )
  return extractTaxonomy(taxonomy)
}

// Overwrites a taxonomy with the given flat option list. POST /taxonomies is a
// full replace keyed on the root's taxonomy_uuid: existing options keep their
// uuid (reused), new options pass null (the backend assigns one), and any
// existing option omitted from the list is deactivated. Returns the saved root
// + options (new options now carry their assigned uuids).
export const saveTaxonomyOptions =
  ({ root, options, organizationId }) =>
  async (dispatch) => {
    const body = {
      taxonomy_uuid: root.taxonomy_uuid,
      name: root.name.trim(),
      description: root.description ?? '',
      requested_organization_id: organizationId,
      sub_categories: options.map((option) => ({
        taxonomy_uuid: option.taxonomy_uuid ?? null,
        name: option.name.trim(),
        description: option.description ?? '',
        sub_categories: [],
      })),
    }
    const saved = await fetchingAPI(
      `${apiService.summary}/taxonomies`,
      'POST',
      dispatch,
      JSON.stringify(body)
    )
    // A gate stores an option's category uuid and reads its name from the org
    // field list, so a rename here leaves every condition chip and branch label
    // showing the old name until that list is refetched. The uuid is stable, so
    // nothing is broken by the staleness — it just reads wrong.
    dispatch(fetchOrgFieldNames(organizationId))
    return extractTaxonomy(saved)
  }

// 500s and FastAPI's 422 validation noise aren't actionable, so they keep the
// caller's generic copy. Every other failure (the 400-level ones) carries a
// human-readable `message` from the backend that's worth surfacing verbatim.
const getApiErrorMessage = (err, genericMessage) => {
  if (err?.status === 500 || err?.status === 422 || isEmpty(err?.message)) {
    return genericMessage
  }
  return err.message
}

const GENERIC_VERIFY_ERROR = 'Could not verify this metadata field'
// The bit value status a verified field comes back with when its key resolved
// and validated (BitFieldValueStatus); anything else is a config problem.
const VALUE_STATUS_COMPLETED = 'completed'

// Dry-runs one voip_metadata field against the playbook's most recent call, so a
// bad metadata key or type is caught before the field is added. Read-only — the
// backend persists nothing. Never throws: a config that doesn't resolve and a
// request that couldn't run both come back as { verified: false, error } so the
// caller can surface the reason and still let the admin add the field.
export const verifyMetadataField =
  ({ fieldName, dataType, metadataKey, playbookCid, organizationId }) =>
  async (dispatch) => {
    const body = {
      playbook_cid: playbookCid,
      field: {
        field_name: fieldName.trim(),
        field_type: 'voip_metadata',
        data_type: dataType || 'string',
        // The metadata key travels as the field's description, the same way it
        // does on save.
        description: metadataKey.trim(),
      },
    }

    // Only scope the request when there's an org to scope it to — a bare
    // `requested_organization_id` param reads as an empty value the backend
    // rejects, where an absent one correctly falls back to the caller's org.
    const params = isNil(organizationId)
      ? ''
      : `?${queryString.stringify({ requested_organization_id: organizationId })}`

    try {
      const result = await fetchingAPI(
        `${apiService.summary}/bit-flows/verify-metadata-field${params}`,
        'POST',
        dispatch,
        JSON.stringify(body)
      )
      if (result?.status === VALUE_STATUS_COMPLETED) {
        return { verified: true, callId: result.call_id, resolvedValue: result.resolved_value }
      }
      // A field that ran but didn't resolve comes back 200 with the parse error,
      // and the call it ran against — which is half the diagnosis.
      return { verified: false, error: result?.error || GENERIC_VERIFY_ERROR, callId: result?.call_id } // prettier-ignore
    } catch (err) {
      // Includes the playbook having no calls to check against (404), which is a
      // reason verification couldn't run rather than a bad config — so there's no
      // call id to report.
      return { verified: false, error: getApiErrorMessage(err, GENERIC_VERIFY_ERROR) }
    }
  }

// The note Notetaker actually wrote for a call, keyed by field name, so a test
// run can be read against what the live configuration produced rather than in
// isolation.
//
// parsed_summary_v2 supersedes parsed_summary where it exists (mirroring the
// call explorer's Summary view). The agent's own edits (parsed_editable_summary)
// are deliberately ignored: the comparison is between two configurations, not
// between a configuration and a human.
//
// Returns null rather than throwing — a call with no note (never processed, or
// processed before this playbook had a configuration) is a normal answer here,
// so it costs the test run nothing and raises no toast.
export const fetchSavedCallNote = (callId) => async (dispatch) => {
  try {
    // The id comes straight from the editable Call/Message ID field, so it is
    // escaped rather than pasted into the path — a non-numeric value would
    // otherwise be able to reach a different route.
    const summary = await fetchingAPI(
      `${apiService.summary}/summaries/${encodeURIComponent(callId)}`,
      'GET',
      dispatch,
      null,
      null,
      { isSilent: true }
    )
    const generated = isEmpty(summary?.parsed_summary_v2)
      ? summary?.parsed_summary
      : summary.parsed_summary_v2

    return isEmpty(generated) ? null : generated
  } catch (err) {
    return null
  }
}

// Whether a call is one this playbook's configuration actually ran on, so a
// note fetched for a hand-typed id isn't read as the "before" of an edit that
// never touched it. Two playbooks commonly share field names (Summary, Outcome),
// so a note from another playbook would otherwise compare cleanly and report
// changes the author didn't make.
//
// Answers true / false / null, where null is "couldn't tell" — a failed lookup,
// or a flow with no playbook to scope by (the non-call channels). A caller
// treats null as permission to compare, so a lookup problem never removes a
// comparison that was probably fine.
export const fetchCallBelongsToPlaybook =
  ({ callId, playbookCid, callType, organizationId } = {}) =>
  async (dispatch, getState) => {
    if (isEmpty(playbookCid)) {
      return null
    }

    const numericCallId = Number(callId)
    if (!Number.isFinite(numericCallId)) {
      return null
    }

    const { organizationid } = getState().currentUser
    const body = {
      organization_id: organizationId || organizationid,
      call_ids: [numericCallId],
      playbook_cids: [playbookCid],
      call_types: [callType || CHANNEL_TYPE.CALL],
    }

    try {
      const { calls = [] } = await fetchingAPI(
        `${apiService.reporting}/api/calls`,
        'POST',
        dispatch,
        JSON.stringify(body),
        null,
        { isSilent: true }
      )
      return !isEmpty(calls)
    } catch (err) {
      return null
    }
  }

// Runs the form's current fields through the backend's bit-flow test endpoint
// and reshapes the per-field results into the { key: value } object that
// AgentSummaryView renders. Uses prompts.loading.promptTests so the same
// spinner used by the legacy prompt-test path lights up.
const GENERIC_TEST_ERROR = 'Failed to run tests'

// A field the run never reached: a gate it depends on didn't pass, or an
// upstream field it needs was itself skipped. Distinct from `failed`, which is
// a field that ran and errored.
const BIT_FIELD_SKIPPED_STATUS = 'skipped'
// The service always sends a reason with a skip; this only covers it not doing
// so, since the view treats a missing reason as "this field wasn't skipped".
const GENERIC_SKIP_REASON = 'A condition on this field was not met'

// The only field types whose taxonomy the test endpoint accepts inline (ad-hoc,
// unsaved). Everything else loads its taxonomy from the DB at execution, so the
// backend rejects an inline one rather than failing later on a uuid it can't find.
const INLINE_TAXONOMY_FIELD_TYPES = ['pick_list', 'categorization']

export const testBitFlow =
  ({
    callId,
    promptFields,
    llmModelId,
    inlineTaxonomies,
    systemPrompt,
    userPrompt,
    maxResponseTokens,
    fieldLinks = [],
    branchingEnabled = false,
  }) =>
  async (dispatch, getState) => {
    dispatch(setPromptLoading({ promptTests: true }))
    try {
      const { rtnPrompt, rtnBitFlow, rtnFieldPrompts } = getState().playbook ?? {}
      // Individual execution ("run on its own prompt") is a Balto-admin concept;
      // non-admins never alter grouping (mirrors the save path).
      const isAdmin = getState().currentUser?.organizationid === 1

      // New fields added in the editor have no prompt_uuid yet. Link them to
      // the flow's shared extraction prompt so they run together in the default
      // prompt instead of each compiling a standalone one — they still run
      // separately from the shown-to-agent fields via show_to_user. Mirrors the
      // save path's extractionPromptUuid derivation.
      const extractionPromptUuid =
        rtnPrompt?.prompt_uuid ||
        rtnBitFlow?.fields?.find((field) => field.field_type === 'extraction' && field.prompt_uuid)
          ?.prompt_uuid

      // Per-prompt model overrides, keyed by prompt_uuid. Lets each prompt in the
      // run use its own model instead of one global model forced on all of them.
      const promptOverrides = {}
      // Inline taxonomies for brand-new pick_list/categorization fields whose
      // options are staged but not yet minted, keyed by a throwaway taxonomy_uuid.
      const inlineTaxonomiesForNewFields = {}

      const fields = promptFields.map((field, index) => {
        // Preserve the field's real type so pick_list/categorization fields are
        // tested with their own selection prompt, not as extraction.
        const fieldType = field.field_type || 'extraction'

        // Mirror the save path's prompt linking so testing runs against the same
        // prompt a save would produce — the backend keys off what we send:
        //   new_grouping    -> mint a fresh dedicated prompt (run on its own)
        //   prompt_uuid=X    -> run on prompt X (own standalone, or the shared one)
        const onSharedPrompt = !field.prompt_uuid || field.prompt_uuid === extractionPromptUuid
        let promptLink
        if (fieldType !== 'extraction') {
          // pick_list/categorization own their selection prompt.
          promptLink = { prompt_uuid: field.prompt_uuid }
        } else if (!isAdmin) {
          // Non-admins can't change grouping — keep the field's prompt, or share.
          promptLink = { prompt_uuid: field.prompt_uuid || extractionPromptUuid }
        } else if (field.individualExecution && onSharedPrompt) {
          // Promoted from the shared prompt: ask the backend to mint a fresh one
          // (new_grouping). It has no uuid yet, so give it a dummy_prompt_uuid we
          // can target the field's own overrides by. The mint copies the shared
          // prompt, so unsaved prompt/token edits are diffed against rtnPrompt.
          const dummyPromptUuid = uuid()
          promptLink = { new_grouping: index, dummy_prompt_uuid: dummyPromptUuid }
          const override = buildChangedPromptContent(field, rtnPrompt)
          if (field.llm_model_id) {
            override.model_id = field.llm_model_id
          }
          if (!isEmpty(override)) {
            promptOverrides[dummyPromptUuid] = override
          }
        } else if (field.individualExecution) {
          // Already standalone — run on (and, for unsaved model/prompt edits,
          // override) its own prompt, diffed against its loaded record.
          promptLink = { prompt_uuid: field.prompt_uuid }
          const override = buildChangedPromptContent(field, rtnFieldPrompts?.[field.prompt_uuid])
          if (field.llm_model_id) {
            override.model_id = field.llm_model_id
          }
          if (!isEmpty(override)) {
            promptOverrides[field.prompt_uuid] = override
          }
        } else if (!onSharedPrompt) {
          // Toggled off from standalone -> run on the overall extraction prompt.
          promptLink = { prompt_uuid: extractionPromptUuid }
        } else {
          // Plain shared extraction field (incl. brand-new fields with no prompt).
          promptLink = { prompt_uuid: field.prompt_uuid || extractionPromptUuid }
        }

        // A saved selection field has a uuid; a brand-new one's options are staged
        // (no uuid yet), so mint a throwaway uuid for the request and send the
        // staged options as an inline taxonomy the test resolves against —
        // otherwise the backend rejects the field for having no taxonomy_uuid.
        //
        // Only the selection types can do this: at execution they read their
        // options off the inline tree, while a voip_metadata field loads its
        // taxonomy from the DB, so the backend rejects an inline one outright. A
        // metadata field whose options aren't saved yet therefore tests
        // unconstrained — the same key/type-only check the create modal runs.
        let taxonomyUuid = field.taxonomy_uuid
        if (
          INLINE_TAXONOMY_FIELD_TYPES.includes(fieldType) &&
          !taxonomyUuid &&
          !isEmpty(field.new_taxonomy_options)
        ) {
          taxonomyUuid = uuid()
          inlineTaxonomiesForNewFields[taxonomyUuid] = {
            name: field.key,
            description: '',
            sub_categories: field.new_taxonomy_options.map((option) => ({
              name: option.name,
              description: option.description ?? '',
              sub_categories: [],
            })),
          }
        }

        return {
          field_name: field.key,
          data_type: field.data_type || 'string',
          field_type: fieldType,
          description: field.value,
          ...promptLink,
          // The backend tests against the realtime transcript when any field is
          // agent-visible, mirroring production processing.
          show_to_user: field.show_to_user ?? false,
          order_id: index,
          // pick_list/categorization fields need their taxonomy to resolve options.
          ...(taxonomyUuid ? { taxonomy_uuid: taxonomyUuid } : {}),
        }
      })

      const requestBody = {
        call_id: Number(callId),
        fields,
        edges: buildEdgePayload(
          branchingEnabled ? withTokenLinks(promptFields, fieldLinks) : fieldLinks
        ),
      }
      // Shared extraction prompt override: the selected model plus any unsaved
      // system/user prompt or max-token edits from the prompt editor (org 1, and
      // only when a real prompt record backs it). Scope it to the shared prompt —
      // a global `overrides` would force every prompt (individual-execution
      // fields, pick_list/categorization) onto it too. If the shared prompt isn't
      // compiled yet (brand-new flow, no uuid to key by), fall back to a global
      // override for the extraction fields.
      const sharedOverride = {}
      if (llmModelId) {
        sharedOverride.model_id = llmModelId
      }
      if (isAdmin && rtnPrompt?.prompt_uuid) {
        Object.assign(
          sharedOverride,
          buildChangedPromptContent(
            {
              system_prompt: systemPrompt,
              user_prompt: userPrompt,
              max_response_tokens: maxResponseTokens,
            },
            rtnPrompt
          )
        )
      }
      if (!isEmpty(sharedOverride)) {
        if (extractionPromptUuid) {
          promptOverrides[extractionPromptUuid] = {
            ...(promptOverrides[extractionPromptUuid] ?? {}),
            ...sharedOverride,
          }
        } else {
          requestBody.overrides = { ...(requestBody.overrides ?? {}), ...sharedOverride }
        }
      }
      if (!isEmpty(promptOverrides)) {
        requestBody.prompt_overrides = promptOverrides
      }

      // A pick_list/categorization field can carry options edited in the UI but
      // not yet saved: unsaved edits to an existing field's taxonomy (keyed by
      // its real uuid) and brand-new fields' staged options (keyed by a throwaway
      // uuid). Pass both as inline_taxonomies so the test runs against them
      // without persisting anything.
      const mergedInlineTaxonomies = {
        ...(inlineTaxonomies ?? {}),
        ...inlineTaxonomiesForNewFields,
      }
      if (!isEmpty(mergedInlineTaxonomies)) {
        requestBody.inline_taxonomies = mergedInlineTaxonomies
      }
      const body = JSON.stringify(requestBody)

      const response = await fetchingAPI(
        `${apiService.summary}/bit-flows/test`,
        'POST',
        dispatch,
        body,
        null,
        // Suppress handleResponse's built-in 500 toast so we own the messaging.
        { isSilent: true }
      )

      // A field can fail in isolation (e.g. the LLM erroring on just that
      // field) while the rest resolve, so collect per-field errors alongside
      // the values and let the view flag the offending fields. fieldResults
      // keeps the unflattened result (resolved vs raw, type, taxonomy status)
      // so the view can show how a taxonomy field's raw answer mapped to an
      // option.
      //
      // A skipped field is flow control, not a fault — a gate it depends on
      // didn't pass, so it never ran. The service returns its reason in the same
      // `error` slot a failure uses, so split them here by status: fieldSkips is
      // what the view reads for both "was this skipped" and "why", which is why
      // a reason is always set even if the service sent none.
      const results = response?.results ?? []
      const parsed_response = {}
      const fieldErrors = {}
      const fieldResults = {}
      const fieldSkips = {}
      results.forEach((result) => {
        parsed_response[result.field_name] = result.resolved_value ?? result.raw_value ?? ''
        fieldResults[result.field_name] = {
          fieldType: result.field_type,
          resolvedValue: result.resolved_value,
          rawValue: result.raw_value,
          taxonomyStatus: result.taxonomy_status,
        }
        if (result.status === BIT_FIELD_SKIPPED_STATUS) {
          fieldSkips[result.field_name] = isEmpty(result.error) ? GENERIC_SKIP_REASON : result.error
        } else if (!isEmpty(result.error)) {
          fieldErrors[result.field_name] = result.error
        }
      })

      return { parsed_response, fieldErrors, fieldResults, fieldSkips }
    } catch (err) {
      toast.error(getApiErrorMessage(err, GENERIC_TEST_ERROR))
      return null
    } finally {
      dispatch(setPromptLoading({ promptTests: false }))
    }
  }

export const createRTNTest = (prompt, promptTest) => async (dispatch) => {
  try {
    const body = JSON.stringify({ ...promptTest, action: 'create' })
    const promptTestResponse = await fetchingAPI(
      `${apiService.summary}/prompts/${prompt.id}/tests`,
      'POST',
      dispatch,
      body
    )
    const promptWithNewTest = {
      ...prompt,
      prompt_tests: [...prompt.prompt_tests, promptTestResponse],
    }
    dispatch(actions.setRTNPrompt(promptWithNewTest))
  } catch (err) {
    toast.error('Failed to create test', { cause: err })
  }
}

export const deleteRTNTest = (prompt, promptTestId) => async (dispatch) => {
  try {
    const promptTestResponse = await fetchingAPI(
      `${apiService.summary}/prompts/${prompt.id}/tests/${promptTestId}`,
      'DELETE',
      dispatch
    )
    const promptWithoutTest = {
      ...prompt,
      prompt_tests: prompt.prompt_tests.filter(
        (promptTest) => promptTest.id !== promptTestResponse.id
      ),
    }
    dispatch(actions.setRTNPrompt(promptWithoutTest))
  } catch (err) {
    toast.error('Failed to delete test', { cause: err })
  }
}

export const getRTNTestResults = (promptId, tempPrompt) => async (dispatch) => {
  dispatch(actions.setLoading({ promptTests: true }))
  try {
    const body = JSON.stringify({ ...tempPrompt, action: 'execute' })
    return await fetchingAPI(
      `${apiService.summary}/prompts/${promptId}/tests`,
      'POST',
      dispatch,
      body
    )
  } catch (err) {
    toast.error('Failed to run tests', { cause: err })
    return []
  } finally {
    dispatch(actions.setLoading({ promptTests: false }))
  }
}

export const generatePrompt = (payload, section) => async (dispatch, getState) => {
  try {
    const playbook = getState().playbook?.playbook
    const configCid = playbook?.cid
    const organizationId = playbook?.organization_id
    const sectionPath = section === accessors.CHECKLIST ? 'checklist' : 'dynamic-prompt'
    const response = await fetchingAPI(
      `${apiService.summary}/playbook/generate-prompt/${sectionPath}`,
      'POST',
      dispatch,
      JSON.stringify({
        ...payload,
        config_cid: configCid,
        requested_organization_id: organizationId,
      })
    )

    return response.generated_prompt
  } catch (error) {
    if (error?.status === 422) {
      return { insufficientData: true }
    }
    toast.error('Failed to generate prompt', { cause: error })
    return undefined
  }
}

export const evaluatePrompt = (prompt) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/playbook/evaluate-prompt`,
      'POST',
      dispatch,
      JSON.stringify({ prompt })
    )

    return response
  } catch (error) {
    toast.error('Failed to evaluate prompt', { cause: error })
    return undefined
  }
}

export const generateChecklistDescription = (prompt) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.summary}/playbook/generate-prompt/checklist/description`,
      'POST',
      dispatch,
      JSON.stringify({ prompt })
    )

    return response.description
  } catch (error) {
    // No toast — the manager can't do anything about a failed description
    // generation, and the field simply stays hidden without one.
    return undefined
  }
}

export const testPrompt = (body) => async (dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.core}/api/llm_checklist/test`,
      'POST',
      dispatch,
      JSON.stringify(body)
    )

    return response
  } catch (error) {
    toast.error('Failed to test prompt', { cause: error })
    return undefined
  }
}

// How many recent calls the test drawer offers to pick from.
export const RECENT_CALLS_LIMIT = 10
// How many it asks the endpoint for. The reporting API applies `limit` server
// side and doesn't promise an order, so asking for exactly ten could hand back
// an arbitrary ten with newer calls already excluded — no amount of sorting
// afterwards recovers them. Ask for a window big enough to sort within.
const RECENT_CALLS_FETCH_LIMIT = 200
// How far back the drawer looks for them. Long enough that a low-volume
// playbook still has something to test against, short enough that the response
// stays small.
const RECENT_CALLS_DAYS = 30

// Identifies which configuration a recent-calls result belongs to. The drawer
// compares this against what it is showing before trusting the list.
export const recentCallsScope = ({ playbookCid, callType, organizationId } = {}) =>
  `${organizationId ?? ''}:${callType ?? ''}:${playbookCid ?? ''}`

// The interactions this configuration has already run on, newest first, so a
// test can be started by picking a real call instead of pasting an id from
// another tab. Never toasts: the list is a convenience, and the Call ID field
// still works without it.
export const fetchRecentCallsForConfig =
  ({ playbookCid, callType, organizationId } = {}) =>
  async (dispatch, getState) => {
    const { organizationid } = getState().currentUser
    // Stamped on the result so a response that lands after the user has moved to
    // another playbook or channel can't be read as this one's calls — which the
    // drawer would otherwise trust enough to skip the membership check.
    const scope = recentCallsScope({
      playbookCid,
      callType,
      organizationId: organizationId || organizationid,
    })
    dispatch(actions.setLoading({ rtnRecentCalls: true }))

    const body = {
      organization_id: organizationId || organizationid,
      start_date: moment().subtract(RECENT_CALLS_DAYS, 'days').startOf('day').toISOString(),
      end_date: moment().endOf('day').toISOString(),
      allow_unlimited_date_range: true,
      call_types: [callType || CHANNEL_TYPE.CALL],
      limit: RECENT_CALLS_FETCH_LIMIT,
    }
    // A non-call channel's flow has no playbook scope (see
    // getSummaryConfigForPlaybook), so there's no cid to narrow by.
    if (!isEmpty(playbookCid)) {
      body.playbook_cids = [playbookCid]
    }

    try {
      const { calls = [] } = await fetchingAPI(
        `${apiService.reporting}/api/calls`,
        'POST',
        dispatch,
        JSON.stringify(body),
        null,
        // The list is a convenience, so own the (silent) messaging rather than
        // letting handleResponse raise an error toast over it.
        { isSilent: true }
      )
      // The endpoint's ordering isn't part of its contract, so sort what came
      // back rather than trusting it to be recent. Timestamps arrive as epoch
      // milliseconds or as an ISO string depending on the response, so they are
      // parsed rather than subtracted raw.
      const recentCalls = [...calls]
        .sort(
          (callA, callB) =>
            (toCallTimestamp(callB.start_time) ?? 0) - (toCallTimestamp(callA.start_time) ?? 0)
        ) // prettier-ignore
        .slice(0, RECENT_CALLS_LIMIT)
      dispatch(actions.setRTNRecentCalls({ scope, calls: recentCalls }))
    } catch (err) {
      dispatch(actions.setRTNRecentCalls({ scope, calls: [] }))
    } finally {
      dispatch(actions.setLoading({ rtnRecentCalls: false }))
    }
  }

export const fetchRTNMetrics =
  ({ organizationId, playbookCid, callType } = {}) =>
  async (dispatch, getState) => {
    const { organizationid } = getState().currentUser
    dispatch(actions.setLoading({ rtnMetrics: true }))
    const startDate = moment().subtract(30, 'days').startOf('day').toISOString()
    const endDate = moment().endOf('day').toISOString()

    const urlParams = {
      requested_organization_id: organizationId || organizationid,
      start_date: startDate,
      end_date: endDate,
    }

    if (playbookCid) {
      urlParams.playbook_cid = playbookCid
    }

    if (callType) {
      urlParams.call_type = callType
    }

    const url = `${apiService.summary}/summaries/metrics?${queryString.stringify(urlParams)}`

    try {
      const metrics = await fetchingAPI(url, 'GET', dispatch)
      dispatch(actions.setRTNMetrics(metrics))
    } catch (err) {
      toast.error('Failed to fetch metrics', { cause: err })
    } finally {
      dispatch(actions.setLoading({ rtnMetrics: false }))
    }
  }
